import type {
  CreateFormFromTemplateInput,
  FormTemplateContract,
  FormTemplateDetailContract,
  FormTemplateListResult,
  FormTemplateSnapshotContract,
} from '@/contracts/form-templates'
import {
  createMockFormFromTemplate,
  createMockFormTemplateFromForm,
  getMockFormTemplateDetail,
  listMockFormTemplates,
  patchMockFormTemplate,
} from '@/lib/mock-api/form-templates'
import type { FormDetailContract } from '@/contracts/questionnaire'
import { apiOrMock, requestApiEnvelope, toSourceMeta } from './http'

export async function listFormTemplates(): Promise<FormTemplateListResult> {
  return apiOrMock({
    label: 'listFormTemplates',
    api: async () => {
      const envelope = await requestApiEnvelope<FormTemplateContract[]>('/api/form-templates')
      return {
        data: envelope.data,
        meta: {
          ...toSourceMeta(envelope.meta),
          total: typeof envelope.meta?.total === 'number' ? envelope.meta.total : envelope.data.length,
        },
      }
    },
    mock: listMockFormTemplates,
  })
}

export async function getFormTemplateDetail(
  templateId: string,
): Promise<FormTemplateDetailContract> {
  return apiOrMock({
    label: 'getFormTemplateDetail',
    api: async () => {
      const envelope = await requestApiEnvelope<FormTemplateDetailContract>(
        `/api/form-templates/${templateId}`,
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => getMockFormTemplateDetail(templateId),
  })
}

export async function saveFormAsTemplate(
  formId: string,
  input: {
    title: string
    description?: string
    category?: string
  },
): Promise<FormTemplateDetailContract> {
  return apiOrMock({
    label: 'saveFormAsTemplate',
    api: async () => {
      const envelope = await requestApiEnvelope<FormTemplateDetailContract>(
        '/api/form-templates/from-form',
        {
          method: 'POST',
          body: JSON.stringify({ ...input, sourceFormId: formId }),
        },
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => createMockFormTemplateFromForm(formId, input),
  })
}

export async function saveFormTemplateSnapshot(
  templateId: string,
  snapshot: FormTemplateSnapshotContract,
): Promise<FormTemplateDetailContract> {
  return apiOrMock({
    label: 'saveFormTemplateSnapshot',
    api: async () => {
      const envelope = await requestApiEnvelope<FormTemplateDetailContract>(
        `/api/form-templates/${templateId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            title: snapshot.form.title,
            description: snapshot.form.description,
            snapshot,
          }),
        },
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () =>
      patchMockFormTemplate(templateId, {
        title: snapshot.form.title,
        description: snapshot.form.description,
        snapshot,
      }),
  })
}

export async function createFormFromTemplate(
  templateId: string,
  input: CreateFormFromTemplateInput = {},
): Promise<FormDetailContract> {
  return apiOrMock({
    label: 'createFormFromTemplate',
    api: async () => {
      const envelope = await requestApiEnvelope<FormDetailContract>(
        `/api/form-templates/${templateId}/create-form`,
        {
          method: 'POST',
          body: JSON.stringify(input),
        },
      )
      return {
        ...envelope.data,
        sourceMeta: toSourceMeta(envelope.meta ?? envelope.data.sourceMeta),
      }
    },
    mock: () => createMockFormFromTemplate(templateId, input),
  })
}
